import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ProjectRouteStationWithParam } from "../shared/interfaces/project-route-station.class";
import { ProjectImageData } from "../shared/interfaces/project-image-data.interface";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { environment } from "../../../environments/environment";
import { DeviceTypeEnum } from "../../device/shared/enums/device-type.enum";
import { BarrierPositionEnum } from "../../device/shared/enums/barrier-position.enum";
import { WeatherDataDisplayValue } from "../../device/shared/interfaces/weatherData-display-value.interface";
import { getWeatherDataDisplayValues } from "../../device/shared/functions/weatherData-display-values";
import { DataEndChannelTypeEnum } from "../../project/shared/enums/data-end-channel-type.enum";
import { VoltageChartComponent } from '../project-schema/shared/components/voltage-chart/voltage-chart.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RadarDataChartComponent } from '../project-schema/shared/components/radar-data-chart/radar-data-chart.component';
import { CurrentChartComponent } from '../project-schema/shared/components/current-chart/current-chart.component';
import { ProjectGroupMetaInformation } from '../shared/interfaces/project-group-meta-information';
import { EngineTrigger } from '../shared/interfaces/engine-trigger.interface';
import { getEngineResultIcon, getEngineResultSourceIcon, getEngineResultStatusClass, getEngineResultStatusText } from '../shared/functions/engine-result-status';
import { SchemaComponentInfo, SchemaDisplayInfo, SchemaRadarInfo, SchemaRouteStationInfo, SchemaStatusLevel, SolarInfo } from '../project-schema/shared/interfaces/schema-route-station-info.interface';
import { of } from 'rxjs';
import { mapSchemaRadarToChartRadar } from '../shared/functions/schema-radar-mapper';
import { SchemaIndicatorEnum } from '../project-schema/shared/enums/schema-indicator.enum';
import { LedModeType } from '../../device/shared/enums/led-mode-type';

@Component({
  selector: 'avs-project-route-station',
  templateUrl: './project-route-station.component.html',
  styleUrls: ['./project-route-station.component.css'],
  animations: [
    trigger('blink', [
      state('off', style({
        opacity: 0
      })),
      state('on', style({
        opacity: 1
      })),
      transition('off <=> on', animate('0.5s'))
    ])
  ]
})
export class ProjectRouteStationComponent implements OnInit, OnDestroy, OnChanges {
  @Input() routeStation?: ProjectRouteStationWithParam;
  @Input() images?: ProjectImageData[] = [];
  @Input() isModalOpen: boolean = false;
  @Input() metaInformation?: ProjectGroupMetaInformation[];
  public url = this.router.url
  public blinkState: string = 'off';
  public rotiationsOngoing: boolean = false;
  protected readonly environment = environment;
  protected readonly DeviceTypeEnum = DeviceTypeEnum;

  /** Vorbereitete Anzeige-Sicht der Station; undefined, wenn der Endpoint keine Daten lieferte. */
  private get info(): SchemaRouteStationInfo | undefined {
    return this.routeStation?.schemaInfo;
  }

  public get isOnline(): boolean {
    return this.info?.online === true;
  }

  public get deviceType(): DeviceTypeEnum | undefined {
    return this.info?.deviceType;
  }

  /**
   * Station ohne Anzeigetyp (DeviceTypeEnum.None) — eigener Getter, weil das
   * Template den Vergleich auch innerhalb von *ngIf-Zweigen braucht, in denen
   * der Compiler `deviceType` bereits auf ungleich None verengt hat.
   */
  public get isDeviceTypeNone(): boolean {
    return this.deviceType === DeviceTypeEnum.None;
  }

  public get isLpCamDevice(): boolean {
    return this.deviceType === DeviceTypeEnum.LPCam;
  }

  public get isDomeCamDevice(): boolean {
    return this.deviceType === DeviceTypeEnum.DomeCam;
  }

  /** Zeitpunkt der letzten Daten der Station (Offline-Tooltip). */
  public get lastDataTime(): string | null | undefined {
    return this.info?.lastData;
  }

  public get stationName(): string | undefined {
    return this.info?.name;
  }

  public get stationSamId(): number | undefined {
    return this.info?.samId;
  }

  public get stationX(): number | undefined {
    return this.info?.x;
  }

  public get stationY(): number | undefined {
    return this.info?.y;
  }

  /** Letzte gemessene Akkuspannung für den Offline-Block. */
  public get offlineVoltageText(): string {
    const voltage = this.info?.solar?.data?.latestVoltage?.voltage;
    return voltage != null ? voltage + 'V' : 'N/A';
  }

  /** Primäre Anzeige der Station (Reihenfolge wie die bisherige led-Sicht). */
  private get primaryDisplay(): SchemaDisplayInfo | undefined {
    return this.info?.displays?.[0];
  }

  public get ledChannelError(): boolean {
    return this.primaryDisplay?.cInfo?.channel?.isInChannelError === true;
  }

  public get ledLinkedMode(): boolean {
    return this.primaryDisplay?.data?.ledBrightness?.mode === LedModeType.LINKED;
  }

  public get ledBrightness(): number | undefined {
    return this.primaryDisplay?.data?.ledBrightness?.brightness;
  }

  public get isLedOn(): boolean {
    return this.primaryDisplay?.data?.display?.indicator === SchemaIndicatorEnum.On;
  }

  public get isLedOff(): boolean {
    return this.primaryDisplay?.data?.display?.indicator === SchemaIndicatorEnum.Off;
  }

  public get ledCode(): number | undefined {
    return this.primaryDisplay?.data?.display?.code;
  }

  public get isLedAnimated(): boolean {
    return this.primaryDisplay?.data?.isAnimated === true;
  }

  /** Prismenwender-Station: Anzeige-Kanal vom Typ PW/PW-Cluster statt LED. */
  public get isPW(): boolean {
    return this.info?.displays?.some(d =>
      d.cInfo.channel.deType === DataEndChannelTypeEnum.PW ||
      d.cInfo.channel.deType === DataEndChannelTypeEnum.PW_CLUSTER) === true;
  }

  private get solarData(): SolarInfo | undefined {
    return this.info?.solar?.data ?? undefined;
  }

  public get solarChannelError(): boolean {
    return this.info?.solar?.cInfos?.some(c => c.channel.isInChannelError === true) === true;
  }

  public get solarDateTime(): string | undefined {
    return this.solarData?.dataDateTime;
  }

  /** Solar-Kanäle für die Panel-Indikatoren (DE 11 = Panel 1, DE 12 = Panel 2). */
  public get solarPanelCInfos(): SchemaComponentInfo[] {
    return this.info?.solar?.cInfos ?? [];
  }

  private get simData() {
    return this.info?.sim?.data ?? undefined;
  }

  public get simSignal(): number | undefined {
    return this.simData?.signal ?? undefined;
  }

  public get simProvider(): string {
    return this.simData?.provider ?? '';
  }

  public get radars(): SchemaRadarInfo[] {
    return this.info?.radars ?? [];
  }

  public get lamp() {
    return this.info?.lamp ?? undefined;
  }

  public get lampChannelError(): boolean {
    return this.lamp?.cInfo?.channel?.isInChannelError === true;
  }

  /** Blinker eingeschaltet (Stellcode 61 und Indicator On). */
  public get isLampActive(): boolean {
    return this.lamp?.data?.code === 61 &&
      this.lamp?.data?.indicator === SchemaIndicatorEnum.On;
  }

  private get fuelcell() {
    return this.info?.fuelcell ?? undefined;
  }

  private get fuelcellData() {
    return this.fuelcell?.data ?? undefined;
  }

  public get hasFuelcellData(): boolean {
    return this.fuelcellData != null;
  }

  public get fuelcellChannelError(): boolean {
    return this.fuelcell?.cInfo?.channel?.isInChannelError === true;
  }

  public get fuelLevelCart1(): number | undefined {
    return this.fuelcellData?.fuelLevel?.[0];
  }

  public get fuelLevelCart2(): number | undefined {
    return this.fuelcellData?.fuelLevel?.[1];
  }

  public get activeCart(): number | undefined {
    return this.fuelcellData?.nbActiveCart;
  }

  /** Message of the external fuel sensor, unless the station is set to ignore it */
  public get isFuelSensorAlarmShown(): boolean {
    return this.fuelcellData?.externalFuelSensorAlarm === true && this.fuelcell?.ignoreExternalFuelSensor !== true;
  }

  public get isFirstCamLpCam(): boolean {
    return this.info?.cameras?.[0]?.camType === DataEndChannelTypeEnum.LPCam;
  }

  public get isFirstCamDomeCam(): boolean {
    return this.info?.cameras?.[0]?.camType === DataEndChannelTypeEnum.DOM_CAMERA;
  }

  /** Messzeitpunkt der Wetterdaten (Tooltip am Wetter-Icon). */
  public get weatherDataTime(): string | undefined {
    return this.info?.weather?.data?.dataDateTime;
  }

  public imageIntervalCollection: any[] = [];
  public imageWidth: number = 0;
  public imageHeight: number = 0;
  public get imagePanelClass(): string {
    return this.isOnline && this.imageWidth > this.imageHeight
      ? 'transparent'
      : 'bg-black';
  }

  public weatherDataDisplayValues: WeatherDataDisplayValue[] = [];

  constructor(public dialog: MatDialog,
    public router: Router) { }

  public ngOnInit(): void {
    setInterval(() => {
      this.blinkState = (this.blinkState === 'off') ? 'on' : 'off';
    }, 1000);
    if (this.url === '/project/station-overview' || this.url === '/device/route-station') {
      this.isModalOpen = true;
    }
  }

  public ngOnDestroy(): void {
    this.imageIntervalCollection.forEach(id => clearInterval(id));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['routeStation']) {
      this.resetImageDimensions();
      // Die Schemaansicht tauscht die Station beim Polling aus, deshalb werden
      // die Wetterdaten hier und nicht einmalig bei der Initialisierung gesetzt.
      this.setWeatherDataDisplayValues();
    }
  }

  public updateImageDimensions(image: HTMLImageElement): void {
    this.imageWidth = image.offsetWidth || image.naturalWidth || 0;
    this.imageHeight = image.offsetHeight || image.naturalHeight || 0;
  }

  public resetImageDimensions(): void {
    this.imageWidth = 0;
    this.imageHeight = 0;
  }

  public rotateImagesPaths(images: ProjectImageData[]): void {
    if (images.length === 0) return;
    this.rotiationsOngoing = true
    images.forEach(image => {
      if (image.isAnimated && image.animatedImageIds!.length > 0) {
        this.rotatePathForId(image)
      }
    });
  }

  public getSystemzustand(): string {
    let status = this.fuelcell?.cInfo?.status;
    if (status != undefined) {
      if (status === SchemaStatusLevel.Ok) {
        return '';
      }
      if (status === SchemaStatusLevel.Warning) {
        return 'bsz-warnung';
      }
      if (status >= SchemaStatusLevel.Error) {
        return 'bsz-fehler';
      }
    }
    return '.bsz-undef'
  }

  public rotatePathForId(image: ProjectImageData): void {
    const myValueList = image.animatedImageIds!;

    let i = 0;
    let idp = setInterval(() => {
      image.path = this.getPathByImageId(myValueList[i++]);
      if (i === myValueList.length) {
        i = 0;
      }
    }, 1000);

    this.imageIntervalCollection.push(idp);
  }

  public getPathByImageId(imageId: number) {
    if (this.images) {
      let imageObject = this.images.find((x) => x.imageId === imageId && x.routeStationId === this.info?.id)
      return imageObject?.path ? imageObject.path : 'upload/images/6480/207 - Custom Stellcode.bmp';
    }
    else return
  }

  public openVoltageChart(): void {
    this.dialog.open(VoltageChartComponent, {
      data: { samId: this.stationSamId, deviceName: this.stationName, solarDateTime: this.solarDateTime },
      height: '77vh',
      width: '98vw'
    });
  }

  public openCurrentChart(): void {
    this.dialog.open(CurrentChartComponent, {
      data: { routeStation: { samId: this.stationSamId }, solarDateTime: this.solarDateTime },
      height: '77vh',
      width: '98vw'
    });
  }

  public openRadarDataChart(): void {
    const radars = this.radars
      .filter(r => r.data != null)
      .map(r => mapSchemaRadarToChartRadar(r));
    this.dialog.open(RadarDataChartComponent, {
      data: { radar$: of(radars) },
      width: '98vw'
    });
  }

  public getBatteryIcon(): string {
    // Fehlender Wert zählt wie bisher als 0 V und fällt auf die niedrigste Stufe
    const voltage = this.solarData?.batteryVoltage ?? 0;
    if (!this.isOnline) {
      return 'assets/icons/battery/battery_unknown_FILL0_wght400_GRAD0_opsz24.svg';
    } else if (voltage > 13) {
      return 'assets/icons/battery/battery_full_alt_FILL0_wght400_GRAD0_opsz24.svg';
    } else if (voltage > 12.5) {
      return 'assets/icons/battery/battery_horiz_075_FILL0_wght400_GRAD0_opsz24.svg';
    } else if (voltage > 12) {
      return 'assets/icons/battery/battery_horiz_050_FILL0_wght400_GRAD0_opsz24.svg';
    } else if (voltage > 11.5) {
      return 'assets/icons/battery/battery_low_FILL0_wght400_GRAD0_opsz24.svg';
    } else {
      return 'assets/icons/battery/battery_very_low_FILL0_wght400_GRAD0_opsz24.svg';
    }
  }

  public getReceptionIcon(): string {
    if (!this.isOnline) {
      return 'assets/icons/signal/signal_disconnected.svg';
    }
    // Fehlender Signalwert zählt wie bisher als 0 dBm (beste Empfangsstufe)
    const signal = this.simData ? +(this.simData.signal ?? 0) : undefined;
    if (signal !== undefined && signal > -66) {
      return 'assets/icons/signal/reception-4.svg';
    } else if (signal !== undefined && signal > -78) {
      return 'assets/icons/signal/reception-3.svg';
    } else if (signal !== undefined && signal > -90) {
      return 'assets/icons/signal/reception-2.svg';
    } else if (signal !== undefined && signal > -102) {
      return 'assets/icons/signal/reception-1_yellow.svg';
    } else {
      return 'assets/icons/signal/reception-1.svg';
    }
  }

  public getStationImage(stellcode: any): string {
    if (stellcode === undefined) return '';
    let path = this.images?.find(i => i.stellcode === +stellcode)?.path?.toString();
    return path ? path : '';
  }

  public get batteryVoltageText(): string {
    const voltage = this.solarData?.batteryVoltage;
    return voltage == null ? 'offline' : voltage.toFixed(2) + 'V';
  }

  public get loadCurrentText(): string {
    if (!this.solarData) {
      return '';
    }
    const current = this.solarData.loadCurrent;
    return current == null ? 'fehler' : current.toFixed(2) + 'A';
  }

  public get batteryCurrentText(): string {
    if (!this.solarData) {
      return '';
    }
    const current = this.solarData.batteryCurrent;
    return current == null ? 'fehler' : current.toFixed(2) + 'A';
  }

  public get batteryCurrentClass(): string {
    if (!this.solarData) {
      return 'text-secondary text-center small';
    }
    if ((this.solarData.batteryCurrent ?? 0) < 0) {
      return 'text-danger text-center small';
    } else {
      return 'text-success text-center small';
    }
  }


  // Tür gilt als offen, solange nicht eindeutig gemeldet ist, dass sie geschlossen ist
  public isDoorOpen() {
    let door = this.info?.digitalIOs?.find(io => io.cInfo.channel.de === 1)
    return door?.data?.activeStatus !== true
  }
  private get barrier() {
    return this.info?.barrier ?? undefined;
  }

  private get barrierData() {
    return this.barrier?.data ?? undefined;
  }

  public isBarrierDataValid() {
    let deChannelType = this.barrier?.cInfo?.channel?.deType;
    return deChannelType === DataEndChannelTypeEnum.LANESWITCH ||
      deChannelType === DataEndChannelTypeEnum.BAR ||
      deChannelType === DataEndChannelTypeEnum.SWING;
  }

  public getIconForBarrierPositioningStatus() {
    let deChannelType = this.barrier?.cInfo?.channel?.deType;
    let pStatus = this.barrierData?.positioningStatus

    let baseName = 'assets/icons/barsAndSignals/dyna';
    if (deChannelType==DataEndChannelTypeEnum.LANESWITCH)
      baseName = baseName + 'switch_';
    else if (deChannelType==DataEndChannelTypeEnum.BAR)
      baseName = baseName + 'bar_';
    else if (deChannelType==DataEndChannelTypeEnum.SWING)
      baseName = baseName + 'swing_';
    
    if (pStatus == BarrierPositionEnum.UnknownStopped) {
        baseName = baseName+'unbekannt.svg';
    } 
    else if (pStatus == BarrierPositionEnum.MovingToPos1Closed) {
      baseName = baseName+'wird_aktiviert.svg';
    } 
    else if (pStatus == BarrierPositionEnum.MovingToPos2Open) {
      baseName = baseName+'wird_deaktiviert.svg';
    } 
    else if (pStatus == BarrierPositionEnum.AtPos1Closed) {
      baseName = baseName+'aktiv.svg';
    } 
    else if (pStatus == BarrierPositionEnum.AtPos2Open) {
      baseName = baseName+'inaktiv.svg';
    } 
    else  {
        baseName = baseName+'verarbeitung.svg';
    }
    return(baseName);
  }

  public isBarrierBlocked()  {
    if (this.barrierData?.errorCode == true ||
            this.barrierData?.localControlEnabled == true ||
            this.barrierData?.securityLockEnabled == true ||
            this.barrierData?.obstacleDetected == true)
            return(true);
    else if (this.barrierData?.errorCode == false &&
            this.barrierData?.localControlEnabled == false &&
            this.barrierData?.securityLockEnabled == false &&
            this.barrierData?.obstacleDetected == false)
            return(false);
    else
      return(undefined);
  }
  public isBarrierWarmUp()   {
      return (this.barrierData?.warmUp == true);
  }

  public getBarrierPositioningBlinkStatus() {
    let pStatus = this.barrierData?.positioningStatus
    if (pStatus == BarrierPositionEnum.MovingToPos1Closed || pStatus == BarrierPositionEnum.MovingToPos2Open)
      return (this.blinkState);
    else
     return('on');
  }

  // Schaltstatus-Badge (EngineResult): Auslöser-Icon, Statusfarbe als Hintergrund
  public getLedEngineResult(): EngineTrigger | undefined {
    return this.primaryDisplay?.data?.display?.engineResult ?? undefined;
  }

  public getLedEngineIcon(): string {
    return getEngineResultIcon(this.getLedEngineResult());
  }

  /** Symbol des auslösenden Elements (Radar bei MQ, Eingang bei DI); leer bei AQ-Auslösern. */
  public getLedEngineSourceIcon(): string {
    return getEngineResultSourceIcon(this.getLedEngineResult());
  }

  public getLedEngineClass(): string {
    return getEngineResultStatusClass(this.getLedEngineResult());
  }

  public getLedEngineTooltip(): string {
    return getEngineResultStatusText(this.getLedEngineResult());
  }

  public getBarrierEngineResult(): EngineTrigger | undefined {
    return this.barrier?.engineResult ?? undefined;
  }

  public getBarrierEngineIcon(): string {
    return getEngineResultIcon(this.getBarrierEngineResult());
  }

  public getBarrierEngineSourceIcon(): string {
    return getEngineResultSourceIcon(this.getBarrierEngineResult());
  }

  public getBarrierEngineClass(): string {
    // Result=Confirmed heißt bei Schranken nur "Kommando quittiert" (DeTyp 51) —
    // solange die Schranke noch fährt, bleibt das Badge gelb; die Endlage bewertet
    // danach IsDataConsistent (grün/rot)
    if (this.getBarrierEngineResult() && this.isBarrierMoving()) { return 'avs-engine-status-amber'; }
    return getEngineResultStatusClass(this.getBarrierEngineResult());
  }

  public getBarrierEngineTooltip(): string {
    let text = getEngineResultStatusText(this.getBarrierEngineResult());
    if (text && this.isBarrierMoving()) { text += ' — Schranke in Bewegung'; }
    return text;
  }

  private isBarrierMoving(): boolean {
    let positioningStatus = this.barrierData?.positioningStatus;
    return positioningStatus === BarrierPositionEnum.MovingToPos1Closed ||
           positioningStatus === BarrierPositionEnum.MovingToPos2Open;
  }

  public get powerPurchaseIcon(): string {
    if (!this.solarData) {
      return 'assets/icons/arrow-left-short-red.svg'
    }
    if ((this.solarData.batteryCurrent ?? 0) < 0) {
      return 'assets/icons/arrow-right-short-red.svg';
    } else {
      return 'assets/icons/arrow-right-short-green.svg';
    }
  }
  private setWeatherDataDisplayValues(): void {
    this.weatherDataDisplayValues = getWeatherDataDisplayValues(this.info?.weather?.data ?? undefined);
  }
}
